import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, MapPin } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ hero, stats }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"], layoutEffect: false });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  const lines = hero?.titleLines || ["Sourcing the World's Best", "Machining Suppliers"];
  const lenis = useLenis();
  const scrollTo = (id) => (lenis ? lenis.scrollTo(id, { offset: -80 }) : document.querySelector(id)?.scrollIntoView({ behavior: "smooth" }));

  return (
    <section id="top" ref={ref} className="relative min-h-[86vh] flex items-center overflow-hidden bg-navy">
      <motion.img
        style={{ y: imgY }}
        src={hero?.backgroundImage}
        alt="Precision CNC machining"
        className="absolute inset-0 w-full h-[115%] object-cover"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-transparent to-navy/30" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-28 md:pt-32 pb-16 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-2 mb-6"
        >
          <MapPin size={15} className="text-barn" />
          <span className="overline text-white/80">{hero?.overline}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, delay: 0.1, ease: EASE }}
          className="font-display font-extrabold text-white tracking-tight leading-[1.06] text-4xl sm:text-6xl lg:text-7xl max-w-4xl"
        >
          {lines.map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.7, ease: EASE }}
          className="mt-6 font-body text-base md:text-lg text-white/75 leading-relaxed max-w-2xl"
        >
          {hero?.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7, ease: EASE }}
          className="mt-8 flex flex-col sm:flex-row gap-3"
        >
          <button onClick={() => scrollTo("#contact")} data-testid="hero-cta-primary"
            className="group font-body font-bold text-sm px-7 py-4 bg-barn text-white rounded-md hover:bg-[#9c3227] transition-colors duration-200 flex items-center justify-center gap-2">
            {hero?.ctaPrimary || "Request a Sourcing Audit"}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
          <button onClick={() => scrollTo("#services")} data-testid="hero-cta-secondary"
            className="font-body font-bold text-sm px-7 py-4 border border-white/30 text-white rounded-md hover:bg-white hover:text-navy transition-colors duration-200">
            {hero?.ctaSecondary || "Explore Capabilities"}
          </button>
        </motion.div>

        {stats?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.8, ease: EASE }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-3xl border-t border-white/15 pt-8"
          >
            {stats.slice(0, 4).map((s, i) => (
              <div key={i}>
                <div className="font-display font-extrabold text-3xl md:text-4xl text-white">{s.value}</div>
                <div className="font-body text-xs md:text-sm text-white/60 mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
